import { AsyncPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MdsButtonComponent, MdsIconButtonComponent } from '@medpacesoftwaredevelopment/designsystem';
import { AttachmentSizeService } from '@services/attachment-size/attachment-size.service';
import { getExtension } from 'mime';
import { maxFileSize, maxFileSizeInMB, maxSizeOfFilesInMB } from 'src/globals';
import { AlertComponent, AlertData } from '../alert/alert.component';
import { DndDirective } from './dnd.directive';
@Component({
    selector: 'medpace-upload',
    templateUrl: './medpace-upload.component.html',
    styleUrls: ['./medpace-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, AsyncPipe, MdsButtonComponent, AlertComponent, DndDirective, MdsIconButtonComponent],
})
export class MedpaceUploadComponent implements OnDestroy {
    ngOnDestroy(): void {
        const file = this.file();
        if (file) {
            this.attachmentSizeService.trackFiles({
                owner: this.inputElement(),
                files: [],
            });
        }
    }
    protected readonly maxFileSizeInMB = maxFileSizeInMB;
    protected readonly maxSizeOfFilesInMB = maxSizeOfFilesInMB;
    sanitizer = inject(DomSanitizer);
    attachmentSizeService = inject(AttachmentSizeService);
    idVal = input<string>('fileDropRef');
    acceptableTypes = input.required<string[]>();
    uploadButtonTitle = input<string>('Upload file');
    inputElement = viewChild<ElementRef<HTMLInputElement>>('fileDropRef');
    inputFile = input<File | null | undefined>();
    inputFileId = input<number | undefined>();
    downloadFileFn = input<(fileId: number) => void>();
    fileId = signal<number | null>(null);

    displayMultipleFilesSizeExceeded = input<boolean | undefined>();

    public readonly fileAlerts: WritableSignal<AlertData[]> = signal([]);

    inputFileChange = effect(() => {
        const inputFile = this.inputFile();
        if (inputFile === undefined) return;

        if (inputFile === null) {
            // only clear the locally selected file, a null input means 'no file selected locally'
            // (e.g. an already persisted file is only referenced by inputFileId), not 'file removed by the user'
            this.clearSelectedFile();
        } else {
            this.file.set(inputFile);
        }
    });
    inputFileIdChange = effect(() => {
        const inputFileId = this.inputFileId();
        if (!inputFileId) {
            this.fileId.set(null);
            return;
        }
        this.fileId.set(inputFileId);
    });
    file = signal<File | null | undefined>(undefined);
    hasFile = computed(() => {
        return !!this.file() || !!this.fileId();
    });
    fileName = computed(() => {
        return this.file()?.name ?? 'document';
    });
    emitStuff = output<File>();
    fileNameEmitter = output<string>();
    onFileChange = effect(() => {
        const file = this.file();
        this.attachmentSizeService.trackFiles({
            owner: this.inputElement(),
            files: file ? [file] : [],
        });

        if (!file) return;
        this.emitStuff.emit(file);
        this.fileNameEmitter.emit(file.name);
    });
    acceptableTypesDisplay = computed(() => {
        return '.' + this.acceptableTypes().reduce((sum, current) => sum + ', .' + current);
    });

    isFileTypeImage = computed(() => {
        const file = this.file();
        if (!file) return false;

        return file.type.indexOf('image') != -1;
    });
    url = computed(() => {
        if (!this.isFileTypeImage()) return null;

        const file = this.file();
        return this.sanitizer.bypassSecurityTrustUrl((window.URL ? URL : webkitURL).createObjectURL(file));
    });
    isAcceptableFileType(fileType: string): boolean {
        return this.acceptableTypes().includes(getExtension(fileType));
    }

    onFileSelected(event: Event | FileList) {
        const fileList: FileList = (event as FileList)?.length
            ? (event as FileList)
            : ((event as Event)?.target as HTMLInputElement)?.files;

        // clean file alerts
        this.fileAlerts.set([]);

        if (fileList.length === 0) return;

        const notSupportedFileNames: string[] = [];

        let files = Array.from({ length: fileList.length }, (_, index) => index)
            .map((index) => {
                const file = fileList.item(index);
                if (!this.isAcceptableFileType(file.type)) {
                    notSupportedFileNames.push(file.name);
                }
                return file;
            })
            .filter((file) => this.isAcceptableFileType(file.type)); // filter out files with wrong extension

        if (files.length > 1) {
            this.fileAlerts.update((alerts) => [
                ...alerts,
                {
                    content: `Only ${files[0].name} will be uploaded, ${files
                        .slice(1)
                        .map((file) => file.name)
                        .join(', ')} could not be uploaded. (File limit - 1)`,
                    type: 'caution',
                    dataCy: 'max-number-of-files-alert',
                },
            ]);
        }

        if (notSupportedFileNames.length > 0) {
            this.fileAlerts.update((alerts) => [
                ...alerts,
                {
                    content: `${notSupportedFileNames.length === 1 ? 'File' : 'Files'} ${notSupportedFileNames.join(', ')} could not be uploaded.
                     ${notSupportedFileNames.length === 1 ? 'File is' : 'Files are'} not in allowed (${this.acceptableTypesDisplay()}) file formats.`,
                    type: 'danger',
                    dataCy: 'wrong-format-alert',
                },
            ]);
        }
        if (files[0].size >= maxFileSize) {
            this.fileAlerts.update((alerts) => [
                ...alerts,
                {
                    content: `File size is above ${maxFileSizeInMB} MB.`,
                    type: 'danger',
                    dataCy: 'max-size-alert',
                },
            ]);
        }

        const file = files[0];
        if (!file) return;
        this.file.set(file);
    }

    /* istanbul ignore next */
    upload() {
        this.inputElement()?.nativeElement.click();
    }

    private clearSelectedFile() {
        this.file.set(null);
        const inputElement = this.inputElement();
        if (inputElement) {
            inputElement.nativeElement.value = '';
        }
        this.fileAlerts.set([]);
    }
    removeFile() {
        this.clearSelectedFile();

        this.fileId.set(null);

        this.emitStuff.emit(null); // must be done here, otherwise file download doesnt work
    }
    downloadFile(event: Event) {
        if (!this.fileId()) return;
        event.stopPropagation();
        this.downloadFileFn()(this.fileId());
    }

    public removeAlert(removedAlert: AlertData) {
        this.fileAlerts.update((alerts) => alerts.filter((alert) => alert !== removedAlert));
    }
}
